import React from 'react';
import { Button, LoadingSpinner, SelectList, FormError, UI, ISelectListOptionsType } from '@gik/ui';
import bemBlock from '@gik/core/utils/bemBlock';
import { Product } from '@gik/core/models/gik/Product';
import { useProducts } from '@gik/checkout';
import IWordpressService from '@gik/core/models/wordpress/WordpressService';
import { ICalendarEntry } from '@gik/calendar/models/Calendar';
import noop from '@gik/core/utils/noop';
import { CalendarClaimFooter } from '../../CalendarClaimFooter';
import { GiftCardTile } from '@gik/calendar/components/GiftCards';
import { useCalendarStore } from '@gik/calendar/store/CalendarStore';
import { useUserStore } from '@gik/core/store/UserStore';
import { translationKeys } from '../../i18n/en';
import { useTranslation } from 'react-i18next';
import { renderPortal } from '@gik/core/utils/RenderPortal';
import { claimsApi } from '@gik/api/calendar';
import { useInkind } from '@gik/api/inkinds/inkind';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { useServicesByEventTypeId } from '@gik/api/calendar/serviceProductBundle';
import { useProductsByServiceProductBundle } from '@gik/api/products/productsByServiceProductBundle';
import { CalendarServiceCategorySlugs } from '@gik/calendar/models/CalendarEventTypes';

interface IGiftCardServiceFormProps extends React.HTMLAttributes<HTMLDivElement> {
  service: IWordpressService;
  entry: ICalendarEntry;
  errorMessage?: React.ReactNode;
  editEventButton: boolean;
  backButton?: boolean;
  buttonsPortal?: () => HTMLElement;
  onBack?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  onEditEvent?: () => void | Promise<void>;
}

export default function GiftCardServiceForm({
  className,
  errorMessage,
  entry,
  editEventButton,
  backButton,
  buttonsPortal,
  onBack = noop,
  onCancel = noop,
  onEditEvent = noop,
  ...otherProps
}: IGiftCardServiceFormProps): React.ReactElement {
  const bem = bemBlock('giftcard-service-form');
  const { t } = useTranslation();

  const [options, setOptions] = React.useState<ISelectListOptionsType<number>[]>();
  const [filteredProducts, setFilteredProducts] = React.useState<Product[]>();
  const [selectedProduct, setSelectedProduct] = React.useState<Product>();
  const [error, setError] = React.useState<string>();
  const setSelectedGiftcard = useCalendarStore(state => state.setSelectedGiftcard);
  const [serviceProductBundleId, setServiceProductBundleId] = React.useState<number>();

  const allowedGiftCardIds = React.useMemo(() => entry.allowedGiftCardIds || [], [entry]);
  const { data: products } = useProducts(allowedGiftCardIds);

  const { data: productBundleGiftCards } = useProductsByServiceProductBundle(serviceProductBundleId);
  const { data: servicesForEventType } = useServicesByEventTypeId(allowedGiftCardIds?.length > 0 ? null : entry.typeId);

  const userId = useUserStore(state => state.id);
  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);

  async function handlePurchase() {
    if (!selectedProduct) {
      setError(t(translationKeys.giftcardRequired));
      UI.notifyError(t(translationKeys.giftcardRequired));
      return;
    }
    setSelectedGiftcard(selectedProduct);
  }

  const _buttons = (
    <CalendarClaimFooter
      onCancel={onCancel}
      backButton={backButton}
      onBack={onBack}
      editEventButton={editEventButton}
      onEditEvent={onEditEvent}
    >
      <Button variant="primary" onClick={handlePurchase} disabled={!selectedProduct}>
        {!userId && <span>Sign Up &amp; Purchase</span>}
        {userId && <span>Purchase</span>}
      </Button>
    </CalendarClaimFooter>
  );

  /**
   * filter giftcards and create an options object if data is ready/changes
   */
  React.useEffect(() => {
    let _products: Product[];

    if (!allowedGiftCardIds?.length) {
      const _giftCardService = servicesForEventType?.find(item =>
        item.slug.endsWith(CalendarServiceCategorySlugs.GiftCard)
      );

      const _serviceProductBundleId = _giftCardService ? _giftCardService['service-product-bundle'][0] : undefined;
      setServiceProductBundleId(_serviceProductBundleId);

      _products = productBundleGiftCards;
    } else {
      _products = allowedGiftCardIds
        .map(id => {
          return products?.find(item => item.id === id);
        })
        .filter(product => product);
    }

    const filteredProducts = _products?.filter(p => p.checkoutType === 'tango-card');

    const _options = filteredProducts?.map(item => ({
      value: item.id,
      name: item.name,
      gridImage: item.gridImage,
    }));

    setOptions(_options);
    setFilteredProducts(filteredProducts);
    // auto select product if there is only one;
    if (filteredProducts?.length === 1) setSelectedProduct(filteredProducts[0]);
  }, [products, entry, servicesForEventType, allowedGiftCardIds, productBundleGiftCards]);

  const isLoading =
    // inkind page is loading
    !inkind ||
    // we have allowedGiftCardIds but the products to display them are still loading
    (allowedGiftCardIds?.length && !products) ||
    // there are no allowedGiftCardIds and we are still loading data for the default giftcards for this event type
    (!allowedGiftCardIds?.length && (!servicesForEventType || !productBundleGiftCards));

  if (isLoading) return <LoadingSpinner center />;

  return (
    <div className={bem(null, 'claim', className)} {...otherProps}>
      {filteredProducts?.length > 0 ? (
        <>
          {/* TODO: i18n */}
          <p className={bem('caption')}>These gift cards have been selected by the page organizer:</p>

          <SelectList<number>
            flex
            value={selectedProduct?.id}
            options={options}
            className={bem('select-list')}
            onChange={v => {
              setSelectedProduct(filteredProducts.find(item => item.id === v));
            }}
            render={(item, selected) => {
              const giftCard = filteredProducts?.find(p => p.id === item.value);
              return <GiftCardTile giftcard={giftCard} selected={selected} />;
            }}
          />

          <FormError message={error || errorMessage} centered />

          {renderPortal(_buttons, buttonsPortal)}
        </>
      ) : (
        <FormError className={bem('error-empty')} message="No giftcards found" centered />
      )}
    </div>
  );
}
